import { Add, Block, Close, Delete, Edit, Warning } from "@mui/icons-material";
import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, Divider, IconButton, Typography, useMediaQuery, useTheme } from "@mui/material";
import { AxiosError } from "axios";
import { FC, ReactNode, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomLoadMore from "../../../common/components/CustomLoadMore";
import CustomSnackbar from "../../../common/components/CustomSnackbar";
import CustomTable from "../../../common/components/CustomTable";
import { permissionsHeadcells } from "../../../common/config/headcells";
import { MAIN_NODES, PERMISSIONS_ACTIONS, PERMISSIONS_OPTIONS } from "../../../common/config/permissions";
import { useAuth } from "../../../common/hooks/useAuth";
import { IRole } from "../../../common/interfaces/roles";
import { ACTIONS } from "../../../common/interfaces/table";
import rolesService from "../../../common/services/rolesService";
import PermissionsDialog from "./PermissionsDialog";

const itemsPerPage = 5;

const ALL_ACTIONS = [
    PERMISSIONS_ACTIONS.VIEW,
    PERMISSIONS_ACTIONS.CREATE,
    PERMISSIONS_ACTIONS.EDIT,
    PERMISSIONS_ACTIONS.DELETE,
    PERMISSIONS_ACTIONS.DOWNLOAD,
    PERMISSIONS_ACTIONS.MASSIVE_DOWNLOAD
]

const ALL_MAIN_NODES = [
    MAIN_NODES.BEEHIVES,
    MAIN_NODES.BEEHIVES_STATS,
    MAIN_NODES.BEEHIVES_ALERTS,
    MAIN_NODES.USERS,
    MAIN_NODES.ROLES,
    MAIN_NODES.AREAS_EXPERTISE
]

export interface IPermission {
    id: string,
    name: string,
    view: string,
    create: string,
    edit: string,
    delete: string,
    download: string
}

interface IPermissionsProps { }

const Permissions: FC<IPermissionsProps> = () => {
    // global
    const { t } = useTranslation();
    const { permissionsState } = useAuth();
    const theme = useTheme();
    const largeMediumScreen = useMediaQuery(theme.breakpoints.up("sm"));
    const smallScreen = useMediaQuery(theme.breakpoints.up("sm"));

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [action, setAction] = useState<ACTIONS>();
    const [roles, setRoles] = useState<IRole[]>([]);
    const [currentRole, setCurrentRole] = useState<IRole | undefined>(undefined);
    const [currentRoleBody, setCurrentRoleBody] = useState<Partial<IRole> | undefined>(undefined);
    const [permissions, setPermissions] = useState<{ [x: string]: string[] }[] | undefined>(undefined);
    const [saveBtnClicked, setSaveBtnClicked] = useState<boolean>(false);

    // dialogs
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogTitle, setDialogTitle] = useState("");
    const [dialogBody, setDialogBody] = useState<ReactNode>(<></>);

    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
    const [messageSnackbar, setMessageSnackbar] = useState<string>("");
    const [responseType, setResponseType] = useState<"success" | "error">("success");

    // load more controls
    const [count, setCount] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    const getRolesList = useCallback(async (
        rolesList: IRole[],
        total: number,
        count?: number,
    ) => {
        try {
            const response = count ?
                await rolesService.getAll((count - 1) * itemsPerPage, itemsPerPage) :
                await rolesService.getAll(0, total);
            let rows = response.content
            setTotalItems(response.pageInfo.totalItems);
            setRoles([...rolesList, ...rows]);
            setPermissions(
                response.content.map(role => {
                    let savePermissions: { [x: string]: string[] } = {}
                    let tempMainNode = ""
                    const perm = [...role.permissions].sort((a, b) => a.localeCompare(b))
                    perm.map(permission => {
                        let mainNode = permission.split("_")[0]
                        Object.assign(savePermissions, {
                            ...savePermissions,
                            [mainNode]: mainNode === tempMainNode ? [...savePermissions[mainNode], permission] : [permission]
                        })
                        tempMainNode = mainNode
                    })
                    return savePermissions;
                })
            )
        }
        catch (error) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.title)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
        finally {
            setIsLoading(false);
        }
    }, [])

    useEffect(() => {
        setIsLoading(true);
        getRolesList([...roles], totalItems, count);
    }, [getRolesList, count])

    // action handler (icon buttons from the table)
    const handleAction = (action: ACTIONS, data: IRole | undefined) => {
        setAction(action);
        setCurrentRole(data);
        switch (action) {
            case "add": {
                setDialogTitle(t("permissionsPage.addDialogTitle"));
                setDialogBody(undefined);
                setOpenDialog(true);
                break;
            }
            case "edit": {
                if (data) {
                    setDialogTitle(t("permissionsPage.editDialogTitle") + '"' + data.name + '"');
                    setDialogBody(undefined);
                    setOpenDialog(true);
                }
                break;
            }
            case "delete": {
                if (data) {
                    setDialogTitle(t("permissionsPage.deleteDialogTitle") + '"' + data.name + '"?'
                    );
                    setDialogBody(
                        <Typography variant="body1">
                            {t("permissionsPage.deleteDialogWarningText")}
                        </Typography>
                    );
                    setOpenDialog(true);
                }
                break;
            }
            default: {
                break;
            }
        }
    };

    // api request handler
    const apiRequest = (data: Partial<IRole> | string | undefined) => {
        if (data && typeof data !== "string") {
            if (action === "add" && data)
                return rolesService.post(data)
            if (action === "edit" && currentRole && currentRole.id)
                return rolesService.put(currentRole.id, data)
            if (action === "delete" && currentRole && currentRole.id)
                return rolesService.delete(currentRole.id)
        } else {
            return Promise.reject()
        }
    }

    // close dialog handler
    const handleClose = () => {
        setSaveBtnClicked(false);
        setOpenDialog(false);
    };

    // confirm dialog handler
    const handleActionConfirm = async () => {
        if (action !== "delete" && currentRoleBody && !currentRoleBody.name)
            return;
        try {
            await apiRequest(action !== "delete" ? currentRoleBody : currentRole);
            handleClose();
            setOpenSnackbar(true);
            setResponseType("success");
            setMessageSnackbar(t("genericSuccess"));
            getRolesList([], action === "delete" ? totalItems - 1 : action === "add" ? totalItems + 1 : totalItems);
        } catch (error: unknown) {
            setOpenSnackbar(true);
            setResponseType("error");
            if (error instanceof AxiosError) {
                setMessageSnackbar(error.response?.data.content.message)
            } else {
                setMessageSnackbar(t("genericError"));
            }
        }
    }

    const setLabelName = (mainNode: string) => {
        const possibleOptions = [
            {
                value: MAIN_NODES.BEEHIVES,
                desc: t("permissionsPage.sections.beehivesTitle")
            },
            {
                value: MAIN_NODES.BEEHIVES_STATS,
                desc: t("permissionsPage.sections.beehivesStatsTitle")
            },
            {
                value: MAIN_NODES.BEEHIVES_ALERTS,
                desc: t("permissionsPage.sections.beehivesAlertsTitle")
            },
            {
                value: MAIN_NODES.USERS,
                desc: t("permissionsPage.sections.usersTitle")
            },
            {
                value: MAIN_NODES.ROLES,
                desc: t("permissionsPage.sections.rolesTitle")
            },
            {
                value: MAIN_NODES.AREAS_EXPERTISE,
                desc: t("permissionsPage.sections.areasExpertiseTitle")
            }
        ]
        return possibleOptions.find(opt => opt.value === mainNode)?.desc || "-"
    }

    const setLabel = (mainNode: string, action: string, value?: string) => {
        const possibleOptions = [
            {
                value: PERMISSIONS_OPTIONS.NO,
                desc: t("permissionsPage.permissionsForm.noOption")
            },
            {
                value: mainNode + "_" + action,
                desc: t("permissionsPage.permissionsForm.yesOption")
            },
            {
                value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.ONLY_OWNED,
                desc: t("permissionsPage.permissionsForm.onlyOwnedOption")
            },
            {
                value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.ONLY_AREA,
                desc: t("permissionsPage.permissionsForm.onlyAreaOption")
            },
            {
                value: mainNode + "_" + action + "_" + PERMISSIONS_OPTIONS.ALL,
                desc: t("permissionsPage.permissionsForm.allOption")
            }
        ]

        return value ? possibleOptions.find(opt => opt.value === value)?.desc : t("permissionsPage.permissionsForm.noOption")
    }

    return <Box sx={{ py: 1, px: 2, minHeight: "calc(100vh - 198px)" }}>
        <Dialog open={openDialog} fullWidth scroll="paper">
            <DialogTitle sx={{ fontWeight: 600 }}>
                {t(dialogTitle)}
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: (theme) => theme.palette.grey[500],
                }}
            >
                <Close />
            </IconButton>
            <DialogContent sx={{ py: 0, px: 3 }}>
                {
                    action === "delete" ? dialogBody :
                        <PermissionsDialog
                            currentRole={currentRole}
                            setCurrentRoleBody={setCurrentRoleBody}
                            mainNodes={ALL_MAIN_NODES}
                            actions={ALL_ACTIONS}
                            isDefaultRolePresent={!!roles.find(role => role.isDefaultForRegistration)}
                            saveBtnClicked={saveBtnClicked}
                        />
                }
            </DialogContent>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    py: 2,
                    px: 3,
                }}
            >
                <Button
                    aria-label="close"
                    variant="outlined"
                    onClick={() => { handleClose(); }}
                >
                    {t("permissionsPage.dialogCloseButton")}
                </Button>
                <Button
                    onClick={() => { handleActionConfirm(); setSaveBtnClicked(true) }}
                    variant="contained"
                >
                    {action === "delete" ? t("permissionsPage.dialogConfirmButton") : t("permissionsPage.dialogSaveButton")}
                </Button>
            </Box>
        </Dialog>
        <CustomSnackbar
            type={responseType}
            message={messageSnackbar}
            open={openSnackbar}
            onClose={() => setOpenSnackbar(false)}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 0 }}>
                {t("permissionsPage.title")}
            </Typography>
            <Box sx={{ mb: largeMediumScreen ? 2 : 0, display: "flex", flexDirection: largeMediumScreen ? "row" : "column" }}>
                {
                    permissionsState &&
                    permissionsState.existCreateRolesPermission &&
                    <Button variant="contained"
                        sx={(theme) => ({
                            [theme.breakpoints.only("xs")]: {
                                minWidth: { xs: 0 },
                                width: { xs: 34 },
                                height: { xs: 32 },
                                padding: { xs: 2 }
                            }
                        })}
                        onClick={() => handleAction(ACTIONS.ADD, undefined)}
                    >
                        <Add sx={{ mr: largeMediumScreen ? 0.5 : 0 }} />
                        {largeMediumScreen && t("permissionsPage.newRoleButton")}
                    </Button>
                }
            </Box>
        </Box>
        {
            roles.map(
                (userRole, i) => <Box key={i} sx={{ my: 2 }}>
                    {i !== 0 && <Divider sx={{ my: 2 }} />}
                    <Box key={i} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: largeMediumScreen ? userRole.isDefaultForRegistration ? 0 : 2 : 1 }}>
                        <Box sx={{ display: "flex", flexDirection: largeMediumScreen ? "row" : "column", alignItems: largeMediumScreen ? "center" : "start", mb: largeMediumScreen ? 2 : 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 600, mb: largeMediumScreen ? 0 : 1 }}>
                                {userRole.name}
                            </Typography>
                            {
                                userRole.isDefaultForRegistration &&
                                <Chip
                                    variant="filled"
                                    color="info"
                                    label={t("permissionsPage.permissionsForm.defaultForRegistration")}
                                    sx={{ ml: largeMediumScreen ? 1 : 0, fontWeight: 600 }}
                                />
                            }
                        </Box>
                        <Box sx={{ mb: largeMediumScreen ? 2 : 0, display: "flex", flexDirection: largeMediumScreen ? "row" : "column" }}>
                            {
                                permissionsState &&
                                permissionsState.existEditRolesPermission &&
                                <Button variant="contained" onClick={() => handleAction(ACTIONS.EDIT, userRole)}
                                    sx={(theme) => ({
                                        [theme.breakpoints.only("xs")]: {
                                            minWidth: { xs: 0 },
                                            width: { xs: 34 },
                                            height: { xs: 32 },
                                            padding: { xs: 2 }
                                        }
                                    })}
                                >
                                    <Edit sx={{ mr: smallScreen ? 0.5 : 0 }} />
                                    {smallScreen && t("permissionsPage.editButtonLabel")}
                                </Button>
                            }
                            {
                                permissionsState &&
                                permissionsState.existDeleteRolesPermission &&
                                <Button color="error" variant="contained"
                                    sx={(theme) => ({
                                        [theme.breakpoints.only("xs")]: {
                                            minWidth: { xs: 0 },
                                            width: { xs: 34 },
                                            height: { xs: 32 },
                                            padding: { xs: 2 }
                                        },
                                        ml: largeMediumScreen ? 2 : 0,
                                        mt: largeMediumScreen ? 0 : 1
                                    })}
                                    disabled={userRole.isDefaultForRegistration || userRole.isDefaultForSuperAdmin}
                                    onClick={() => handleAction(ACTIONS.DELETE, userRole)}
                                >
                                    <Delete sx={{ mr: smallScreen ? 0.5 : 0 }} />
                                    {smallScreen && t("permissionsPage.deleteButtonLabel")}
                                </Button>
                            }
                        </Box>
                    </Box>
                    {
                        userRole.isDefaultForRegistration && <Typography variant="body2" sx={{ fontWeight: 500, display: "flex", alignItems: "center", mb: largeMediumScreen ? 2 : 1 }}>
                            <Warning color='primary' sx={{ mr: 0.5 }} />
                            {t("permissionsPage.permissionsForm.cannotDeleteDefaultRole")}
                        </Typography>
                    }
                    {
                        permissions && <CustomTable
                            headcells={permissionsHeadcells}
                            rows={ALL_MAIN_NODES.map(
                                (mainNode) => {
                                    return {
                                        id: userRole.id,
                                        name: setLabelName(mainNode),
                                        view: permissions[i] && permissions[i][mainNode] ? setLabel(mainNode, PERMISSIONS_ACTIONS.VIEW, permissions[i][mainNode].find(perm => perm.includes(mainNode + "_" + PERMISSIONS_ACTIONS.VIEW))) || t("permissionsPage.permissionsForm.noOption") : t("permissionsPage.permissionsForm.noOption"),
                                        create: permissions[i] && permissions[i][mainNode] ? setLabel(mainNode, PERMISSIONS_ACTIONS.CREATE, permissions[i][mainNode].find(perm => perm.includes(mainNode + "_" + PERMISSIONS_ACTIONS.CREATE))) || t("permissionsPage.permissionsForm.noOption") : t("permissionsPage.permissionsForm.noOption"),
                                        edit: permissions[i] && permissions[i][mainNode] ? setLabel(mainNode, PERMISSIONS_ACTIONS.EDIT, permissions[i][mainNode].find(perm => perm.includes(mainNode + "_" + PERMISSIONS_ACTIONS.EDIT))) || t("permissionsPage.permissionsForm.noOption") : t("permissionsPage.permissionsForm.noOption"),
                                        delete: permissions[i] && permissions[i][mainNode] ? setLabel(mainNode, PERMISSIONS_ACTIONS.DELETE, permissions[i][mainNode].find(perm => perm.includes(mainNode + "_" + PERMISSIONS_ACTIONS.DELETE))) || t("permissionsPage.permissionsForm.noOption") : t("permissionsPage.permissionsForm.noOption"),
                                        download: permissions[i] && permissions[i][mainNode] ? setLabel(mainNode, PERMISSIONS_ACTIONS.DOWNLOAD, permissions[i][mainNode].find(perm => perm.includes(mainNode + "_" + PERMISSIONS_ACTIONS.DOWNLOAD))) || t("permissionsPage.permissionsForm.noOption") : t("permissionsPage.permissionsForm.noOption"),
                                        massive_download: permissions[i] && permissions[i][mainNode] ? setLabel(mainNode, PERMISSIONS_ACTIONS.MASSIVE_DOWNLOAD, permissions[i][mainNode].find(perm => perm.includes(mainNode + "_" + PERMISSIONS_ACTIONS.MASSIVE_DOWNLOAD))) || t("permissionsPage.permissionsForm.noOption") : t("permissionsPage.permissionsForm.noOption"),
                                        actions: [],
                                    }
                                }
                            )}
                            hasCustomStyles={true}
                            pagination={undefined}
                        />
                    }
                </Box>
            )
        }
        {
            roles.length === 0 &&
            <Typography sx={{ fontWeight: 600, display: 'flex', alignItems: "center", mt: 2, ml: 1 }}>
                <Block sx={{ mr: 1 }} />
                {t("beehivesPage.noDataLabel")}
            </Typography>
        }
        {
            !currentRole &&
            Math.round(totalItems / itemsPerPage) >= count &&
            totalItems > itemsPerPage &&
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <CustomLoadMore count={count} setCount={setCount} loading={isLoading} />
            </Box>
        }
    </Box>
}

export default Permissions;